import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Form, Table } from 'react-bootstrap';
import { FaBuilding, FaUsers, FaCalendarAlt, FaChartLine, FaEye, FaSyncAlt, FaTable, FaUtensils, FaClock, FaHashtag, FaSignOutAlt } from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useResumenCorporativo } from '../../services/hooks/useResumenCorporativo';
import { useAuth } from '../../services/hooks/useAuth';

const ResumenCorporativo = () => {
  const { resumen, loading, error, fetchResumen, fetchResumenPorRango, clearError } = useResumenCorporativo();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Configurar fechas por defecto: hoy y 5 días atrás
  const hoy = new Date();
  const cincodiasAtras = new Date();
  cincodiasAtras.setDate(hoy.getDate() - 5);

  const [fechaDesde, setFechaDesde] = useState(cincodiasAtras.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(hoy.toISOString().split('T')[0]);
  const debounceTimer = useRef(null);

  const handleFechaDesdeChange = (event) => {
    const nuevaFecha = event.target.value;
    setFechaDesde(nuevaFecha);
  };

  const handleFechaHastaChange = (event) => {
    const nuevaFecha = event.target.value;
    setFechaHasta(nuevaFecha);
  };

  // Función interna para ejecutar el refresh
  const executeRefresh = useCallback(async () => {
    try {
      if (fechaDesde === fechaHasta) {
        await fetchResumen(fechaHasta);
      } else {
        await fetchResumenPorRango(fechaDesde, fechaHasta);
      }
    } catch (error) {
      console.error('Error in handleRefresh:', error);
    }
  }, [fechaDesde, fechaHasta, fetchResumen, fetchResumenPorRango]);

  // Función con debouncing para evitar clicks múltiples
  const handleRefresh = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Si hay un timer activo, cancelarlo
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Si ya está cargando, no hacer nada
    if (loading) {
      return;
    }

    // Ejecutar el refresh inmediatamente
    executeRefresh();
  }, [executeRefresh, loading]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // NO cargar datos automáticamente al montar
  // Los datos solo se cargarán cuando el usuario haga clic en "Actualizar" o cambie las fechas

  // Hook para ocultar columna fecha en móviles como respaldo
  useEffect(() => {
    const handleMobileColumns = () => {
      const isMobile = window.innerWidth <= 500;
      const fechaColumns = document.querySelectorAll('.hide-mobile');

      fechaColumns.forEach(col => {
        if (isMobile) {
          col.style.display = 'none';
          col.style.visibility = 'hidden';
          col.style.width = '0';
          col.style.minWidth = '0';
          col.style.maxWidth = '0';
          col.style.padding = '0';
          col.style.margin = '0';
          col.style.border = 'none';
        } else {
          col.style.display = '';
          col.style.visibility = '';
          col.style.width = '';
          col.style.minWidth = '';
          col.style.maxWidth = '';
          col.style.padding = '';
          col.style.margin = '';
          col.style.border = '';
        }
      });
    };

    // Ejecutar al cargar
    handleMobileColumns();

    // Ejecutar cuando cambie el tamaño de ventana
    window.addEventListener('resize', handleMobileColumns);

    // Ejecutar cuando se actualicen los datos (después de un breve delay)
    const timeoutId = setTimeout(handleMobileColumns, 100);

    return () => {
      window.removeEventListener('resize', handleMobileColumns);
      clearTimeout(timeoutId);
    };
  }, [resumen]); // Se ejecuta cuando cambian los datos

  // No mostrar pantalla de loading completa durante refresh, solo durante la carga inicial
  if (loading && !resumen) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3">Cargando resumen corporativo...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger" dismissible onClose={clearError}>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={handleRefresh}>
            <FaSyncAlt className="me-2" />
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Estilos específicos para Vista Corporativa */}
      <style>
        {`
          .corporativo-header {
            background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
          }

          .stat-card {
            background: white;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
            border: none;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            height: 100%;
          }

          .stat-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          }

          .stat-icon {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;
            font-size: 1.5rem;
          }

          .stat-number {
            font-size: 2.5rem;
            font-weight: 700;
            color: #2d3748;
            margin: 0;
          }

          .stat-label {
            color: #718096;
            font-size: 0.9rem;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .project-card {
            background: white;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
            margin-bottom: 1.5rem;
            transition: all 0.3s ease;
          }

          .project-card:hover {
            border-color: #667eea;
            box-shadow: 0 5px 20px rgba(102, 126, 234, 0.1);
          }

          .project-header {
            background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
            border-bottom: 1px solid #e2e8f0;
            padding: 1rem 1.5rem;
            border-radius: 12px 12px 0 0;
          }

          .turno-badge {
            background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.8rem;
            font-weight: 600;
            display: inline-block;
            margin-right: 0.5rem;
            margin-bottom: 0.5rem;
          }

          .cerrado-badge {
            background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
          }

          .abierto-badge {
            background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
          }

          .corporativo-title {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-weight: 700;
          }

          .corporativo-logout-btn {
            background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 0.5rem 1rem;
            font-weight: 600;
            box-shadow: 0 4px 15px rgba(229, 62, 62, 0.3);
            transition: all 0.3s ease;
            font-size: 0.9rem;
          }

          .corporativo-logout-btn:hover {
            background: linear-gradient(135deg, #c53030 0%, #9c2626 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(229, 62, 62, 0.4);
            color: white;
          }

          /* Animaciones suaves para las transiciones */
          .data-container {
            transition: all 0.3s ease;
          }

          .data-container.updating {
            opacity: 0.7;
            transform: scale(0.99);
          }

          .stat-card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .stat-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          }

          .stat-number {
            transition: all 0.4s ease;
          }

          .table-responsive {
            transition: opacity 0.3s ease;
          }

          .updating .table-responsive {
            opacity: 0.6;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .data-loaded {
            animation: fadeInUp 0.5s ease;
          }

          .title-logout-container {
            gap: 1rem;
          }

          @media (max-width: 768px) {
            .title-logout-container {
              flex-direction: column !important;
              gap: 0.75rem;
            }

            .corporativo-title {
              font-size: 1.75rem;
              margin-bottom: 0.5rem !important;
              order: 1;
            }

            .corporativo-logout-btn {
              padding: 0.4rem 0.8rem;
              font-size: 0.85rem;
              order: 2;
              align-self: center;
            }
          }

          .date-control {
            background: white;
            border: 2px solid #e2e8f0;
            border-radius: 10px;
            padding: 0.75rem;
            transition: border-color 0.3s ease;
          }

          .date-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }

          /* Optimizaciones específicas para iPhone y móviles similares */
          @media (max-width: 500px) {
            .corporativo-title {
              font-size: 1.4rem !important;
            }

            .table-responsive {
              font-size: 0.75rem;
              border-radius: 8px;
            }

            /* Ocultar completamente la columna fecha en móvil con máxima especificidad */
            .table-responsive .table-resumen .hide-mobile,
            .table-responsive .table-resumen th.hide-mobile,
            .table-responsive .table-resumen td.hide-mobile,
            .table-resumen tbody tr td.hide-mobile,
            .table-resumen thead tr th.hide-mobile {
              display: none !important;
              visibility: hidden !important;
              width: 0 !important;
              min-width: 0 !important;
              max-width: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
            }


            /* Proyecto con estilo compacto - 35% del ancho */
            .table-resumen .proyecto-col {
              width: 35%;
              max-width: none;
              word-break: break-word;
              font-size: 0.7rem;
              padding: 0.4rem 0.2rem;
            }

            /* Servicio + Comedor - 45% del ancho */
            .table-resumen .servicio-col {
              width: 45%;
              max-width: none;
              font-size: 0.65rem;
              padding: 0.4rem 0.2rem;
            }

            /* Cantidad - 20% del ancho */
            .table-resumen .cantidad-col {
              width: 20%;
              max-width: none;
              text-align: center;
              padding: 0.4rem 0.2rem;
              font-weight: bold;
              color: #059669;
              font-size: 0.8rem;
            }

            /* Mostrar fecha en móvil dentro de la columna servicio */
            .mobile-date,
            small.mobile-date {
              font-size: 0.6rem !important;
              color: #374151 !important;
              margin-top: 2px;
              font-weight: 500 !important;
            }

            .table-responsive table thead th,
            .table-responsive table tbody td {
              padding: 0.5rem 0.25rem;
              vertical-align: middle;
            }

            .stat-card {
              margin-bottom: 0.75rem;
            }

            .stat-number {
              font-size: 1.6rem;
            }

            .stat-label {
              font-size: 0.75rem;
            }

            .date-control {
              font-size: 1rem;
              padding: 0.6rem;
            }
          }

          /* Para pantallas muy pequeñas: vista de cards alternativa */
          @media (max-width: 375px) {
            .table-mobile-cards {
              display: none;
            }

            .mobile-cards-view {
              display: block !important;
            }

            .mobile-card-item {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 0.75rem;
              margin-bottom: 0.5rem;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .mobile-card-header {
              display: flex;
              justify-content: between;
              align-items: center;
              margin-bottom: 0.5rem;
              padding-bottom: 0.5rem;
              border-bottom: 1px solid #f3f4f6;
            }

            .mobile-card-proyecto {
              font-weight: 600;
              color: #1f2937;
              font-size: 0.85rem;
              flex: 1;
            }

            .mobile-card-cantidad {
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: white;
              padding: 0.25rem 0.5rem;
              border-radius: 12px;
              font-weight: 600;
              font-size: 0.8rem;
            }

            .mobile-card-details {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 0.5rem;
              font-size: 0.75rem;
            }

            .mobile-card-detail {
              display: flex;
              flex-direction: column;
            }

            .mobile-card-label {
              color: #6b7280;
              font-size: 0.65rem;
              text-transform: uppercase;
              font-weight: 500;
              margin-bottom: 0.125rem;
            }

            .mobile-card-value {
              color: #374151;
              font-weight: 500;
            }
          }

          /* Touch targets mejorados para móvil */
          @media (max-width: 768px) {
            .btn {
              min-height: 44px; /* Estándar iOS para touch targets */
            }

            .table-responsive {
              -webkit-overflow-scrolling: touch;
              border-radius: 10px;
            }

            /* Mejorar legibilidad de la tabla en móvil */
            .table-responsive table {
              margin-bottom: 0;
            }

            .table-responsive table thead th {
              background: #f8fafc;
              position: sticky;
              top: 0;
              z-index: 10;
            }
          }

          /* Tabla de Resumen */
          .table-resumen {
            border-collapse: separate;
            border-spacing: 0;
            background: white;
            border-radius: 0.75rem;
            overflow: hidden;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
            margin-bottom: 2rem;
          }

          .table-resumen thead {
            background: #374151 !important;
            border-bottom: 3px solid #4f46e5 !important;
          }

          .table-resumen thead th {
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            letter-spacing: 0.025em !important;
            text-transform: uppercase !important;
            border: none !important;
            padding: 1.25rem 1rem !important;
            position: relative !important;
            background: none !important;
          }

          .table-resumen thead th::after {
            content: '';
            position: absolute;
            right: 0;
            top: 25%;
            height: 50%;
            width: 1px;
            background: rgba(255, 255, 255, 0.2);
          }

          .table-resumen thead th:last-child::after {
            display: none;
          }

          .table-resumen tbody tr {
            transition: all 0.2s ease-in-out;
            border-bottom: 1px solid #f3f4f6;
          }

          .table-resumen tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(79, 70, 229, 0.02) 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .table-resumen tbody tr:last-child {
            border-bottom: none;
          }

          .table-resumen tbody td {
            padding: 1.25rem 1rem;
            vertical-align: middle;
            border: none;
            font-size: 0.9rem;
            line-height: 1.5;
          }

          .header-icon {
            display: inline-flex !important;
            align-items: center !important;
            gap: 0.5rem !important;
            color: white !important;
          }

          .badge-estado {
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.8rem;
            font-weight: 600;
          }

          .badge-abierto {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
          }

          .badge-cerrado {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
          }

          @media (max-width: 768px) {
            .corporativo-header {
              padding: 1.5rem 0;
              margin-bottom: 1.5rem;
            }

            .stat-number {
              font-size: 2rem;
            }

            .stat-icon {
              width: 50px;
              height: 50px;
              font-size: 1.2rem;
            }

            .project-card {
              margin-bottom: 1rem;
            }

            .table-resumen {
              font-size: 0.8rem;
            }

            .table-resumen thead th,
            .table-resumen tbody td {
              padding: 0.75rem 0.5rem;
            }
          }
        `}
      </style>

      <Container fluid>
        <ToastContainer />

        <Container>
          {/* Título Principal */}
          <Row className="mb-4 mt-4">
            <Col xs={12} className="text-center">
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 title-logout-container">
                <div></div> {/* Espaciador izquierdo */}
                <h1 className="corporativo-title mb-0">
                  <FaEye className="me-3" />
                  Vista Corporativa
                </h1>
                <Button
                  className="corporativo-logout-btn d-flex align-items-center"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt className="me-2" />
                  Cerrar Sesión
                </Button>
              </div>
              <p className="text-muted mb-0">
                Resumen gerencial de comensales por proyecto y turno
              </p>
            </Col>
          </Row>

          {/* Filtro de Fechas Centrado */}
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="stat-card">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col xs={12} md={10} className="mx-auto">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleRefresh(e);
                        }}
                        autoComplete="off"
                      >
                        <Row className="align-items-end">
                          <Col xs={12} sm={5}>
                            <Form.Group>
                              <Form.Label className="form-label-dh fw-bold">
                                <FaCalendarAlt className="me-2" />
                                Fecha Desde:
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={fechaDesde}
                                onChange={handleFechaDesdeChange}
                                className="date-control"
                                required
                                max={fechaHasta}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12} sm={5}>
                            <Form.Group>
                              <Form.Label className="form-label-dh fw-bold">
                                <FaCalendarAlt className="me-2" />
                                Fecha Hasta:
                              </Form.Label>
                              <Form.Control
                                type="date"
                                value={fechaHasta}
                                onChange={handleFechaHastaChange}
                                className="date-control"
                                required
                                min={fechaDesde}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12} sm={2} className="text-center">
                            <Button
                              type="submit"
                              variant="primary"
                              disabled={loading}
                              className="w-100 mt-3 mt-sm-0"
                              style={{
                                background: loading
                                  ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '0.75rem'
                              }}
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Actualizando...
                                </>
                              ) : (
                                <>
                                  <FaSyncAlt className="me-2" />
                                  Actualizar
                                </>
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {resumen && (
            <div className={`data-container ${loading ? 'updating' : 'data-loaded'}`}>
              {/* Tarjetas de Estadísticas */}
              <Row className="mb-4">
                <Col xs={6} md={4}>
                  <Card className="stat-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon">
                        <FaBuilding />
                      </div>
                      <h2 className="stat-number">{resumen.totales?.total_proyectos || 0}</h2>
                      <p className="stat-label">Proyectos Activos</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="stat-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon">
                        <FaUsers />
                      </div>
                      <h2 className="stat-number">{resumen.totales?.total_comensales || 0}</h2>
                      <p className="stat-label">Atenciones</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="stat-card">
                    <Card.Body className="text-center">
                      <div className="stat-icon">
                        <FaCalendarAlt />
                      </div>
                      <h2 className="stat-number">{resumen.totales?.total_turnos_hoy || 0}</h2>
                      <p className="stat-label">Turnos Atendidos</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Detalle por Proyecto */}
              <Row>
                <Col xs={12}>
                  <h3 className="corporativo-title mb-4">
                    <FaTable className="me-2" />
                    Detalle por Proyecto
                  </h3>

                  {/* Tabla Resumen */}
                  {resumen.resumen_por_proyectos && resumen.resumen_por_proyectos.length > 0 && (
                    <>
                      {/* Vista de tabla para pantallas medianas y grandes */}
                      <Card className="stat-card mb-4 table-mobile-cards">
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <Table className="table-resumen mb-0">
                              <thead>
                                <tr>
                                  <th className="proyecto-col">
                                    <span className="header-icon">
                                      <FaBuilding />
                                      Proyecto
                                    </span>
                                  </th>
                                  <th className="hide-mobile">
                                    <span className="header-icon">
                                      <FaCalendarAlt />
                                      Fecha
                                    </span>
                                  </th>
                                  <th className="servicio-col">
                                    <span className="header-icon">
                                      <FaUtensils />
                                      Servicio
                                    </span>
                                  </th>
                                  <th className="cantidad-col">
                                    <span className="header-icon">
                                      <FaUsers />
                                      Cantidad
                                    </span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {resumen.resumen_por_proyectos.map((proyecto) =>
                                  proyecto.turnos.map((turno) => (
                                    <tr key={turno.turno_cabecera_id}>
                                      <td className="proyecto-col">
                                        <strong className="text-primary">
                                          {proyecto.proyecto_nombre}
                                        </strong>
                                      </td>
                                      <td className="hide-mobile">{turno.fecha}</td>
                                      <td className="servicio-col">
                                        <div className="d-flex flex-column">
                                          <strong>{turno.servicio}</strong>
                                          <small className="text-muted">{turno.comedor}</small>
                                          <small className="d-block d-sm-none mobile-date">{turno.fecha}</small>
                                        </div>
                                      </td>
                                      <td className="cantidad-col">
                                        <span className="fw-bold text-success">
                                          {turno.cantidad_comensales}
                                        </span>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>

                      {/* Vista de cards para pantallas muy pequeñas */}
                      <div className="mobile-cards-view d-none">
                        {resumen.resumen_por_proyectos.map((proyecto) =>
                          proyecto.turnos.map((turno) => (
                            <div key={turno.turno_cabecera_id} className="mobile-card-item">
                              <div className="mobile-card-header">
                                <div className="mobile-card-proyecto">
                                  <FaBuilding className="me-1" />
                                  {proyecto.proyecto_nombre}
                                </div>
                                <div className="mobile-card-cantidad">
                                  {turno.cantidad_comensales}
                                </div>
                              </div>
                              <div className="mobile-card-details">
                                <div className="mobile-card-detail">
                                  <span className="mobile-card-label">Fecha</span>
                                  <span className="mobile-card-value">{turno.fecha}</span>
                                </div>
                                <div className="mobile-card-detail">
                                  <span className="mobile-card-label">Servicio</span>
                                  <span className="mobile-card-value">{turno.servicio}</span>
                                </div>
                                <div className="mobile-card-detail">
                                  <span className="mobile-card-label">Comedor</span>
                                  <span className="mobile-card-value">{turno.comedor}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}

                  {(!resumen.resumen_por_proyectos || resumen.resumen_por_proyectos.length === 0) && (
                    <Alert variant="info">
                      <FaCalendarAlt className="me-2" />
                      No hay datos disponibles para el período seleccionado
                    </Alert>
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Container>
      </Container>
    </>
  );
};

export default ResumenCorporativo;